import { useEffect, useState } from "react";
import { Loader2, AlertTriangle, RotateCcw, CalendarDays } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr, isAdminTestModeForced } from "../lib/api";
import { useQuota } from "../context/QuotaContext";

/**
 * Baja de la suscripción, con encuesta obligatoria delante.
 *
 * La encuesta no está para poner trabas: está para saber POR QUÉ se va la gente, que es el único
 * dato del embudo que no se puede reconstruir mirando el uso. Por eso es un paso y no tres, y por
 * eso el botón de cancelar nunca se esconde ni se desactiva — quien quiere irse se va, y si le
 * cuesta trabajo lo que se pierde es la respuesta, no la baja.
 *
 * La lista de motivos viene del servidor (MOTIVOS_BAJA en server.py) y no se duplica aquí: si se
 * añade uno nuevo aparece solo, y lo ya guardado sigue agrupando igual.
 *
 * Nunca se corta de golpe. La baja se difiere al final del ciclo ya pagado, así que el usuario
 * conserva lo que pagó hasta el último día — y hasta entonces puede deshacerla desde aquí mismo.
 */
export default function BajaSuscripcion() {
  const { quota, cancelPendingChange, refreshQuota } = useQuota();
  const [motivos, setMotivos] = useState([]);
  const [abierto, setAbierto] = useState(false);
  const [motivo, setMotivo] = useState("");
  const [comentario, setComentario] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [renovacion, setRenovacion] = useState(null);

  useEffect(() => {
    if (!abierto || motivos.length) return;
    api.get("/payments/motivos-baja").then((r) => setMotivos(r.data?.motivos || [])).catch(() => {});
  }, [abierto, motivos.length]);

  const tienePlan = !!quota?.has_subscription;
  const pendiente = quota?.pending_plan_change;
  const yaDeBaja = pendiente?.plan === "gratis";

  useEffect(() => {
    if (!tienePlan || yaDeBaja) return;
    let activo = true;
    api.get("/payments/subscription", { params: { origin_url: window.location.origin } })
      .then((r) => {
        if (activo) setRenovacion(r.data?.renews_at || null);
      })
      .catch(() => {
        if (activo) setRenovacion(null);
      });
    return () => { activo = false; };
  }, [tienePlan, yaDeBaja]);

  const fechaLarga = (valor) => valor
    ? new Intl.DateTimeFormat("es-ES", { day: "numeric", month: "long", year: "numeric" })
      .format(new Date(valor))
    : null;

  // Ya pidió la baja: aquí no se le vuelve a ofrecer cancelar, se le ofrece volver.
  if (yaDeBaja) {
    return (
      <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4 sm:p-5" data-testid="baja-pendiente">
        <div className="flex gap-3">
          <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <h3 className="text-base font-bold text-amber-900">Tu suscripción se cancelará al final del ciclo</h3>
            <p className="mt-1 text-sm text-amber-900/90">
              Conservas tu plan y tus créditos hasta{" "}
              <b>{pendiente?.effective_at ? new Date(pendiente.effective_at).toLocaleDateString() : "el final del periodo pagado"}</b>.
              Después pasarás al plan Gratis. Puedes volver atrás cuando quieras.
            </p>
            <button data-testid="baja-deshacer"
              onClick={async () => {
                try { await cancelPendingChange(); toast.success("Cancelación anulada. Sigues con tu plan."); }
                catch (e) { toast.error(apiErr(e)); }
              }}
              className="mt-3 inline-flex items-center gap-2 rounded-lg bg-amber-600 px-4 py-2 text-sm font-bold text-white hover:bg-amber-700">
              <RotateCcw size={16} /> Quiero seguir con mi plan
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!tienePlan) return null;

  const enviar = async () => {
    if (!motivo) return;
    setEnviando(true);
    try {
      await api.post("/payments/baja", { motivo, comentario, origin_url: window.location.origin, force_test: isAdminTestModeForced() });
      await refreshQuota();
      setAbierto(false); setMotivo(""); setComentario("");
      toast.success("Cancelación programada para el final de tu ciclo.");
    } catch (e) { toast.error(apiErr(e)); }
    finally { setEnviando(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5" data-testid="baja-suscripcion">
      {renovacion ? (
        <div className="mb-4 flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 px-3.5 py-3"
          data-testid="proxima-renovacion">
          <CalendarDays size={19} className="shrink-0 text-blue-600" />
          <div className="min-w-0">
            <p className="text-xs font-semibold uppercase tracking-wide text-blue-700">Próxima renovación</p>
            <p className="text-sm font-bold text-slate-900">{fechaLarga(renovacion)}</p>
          </div>
        </div>
      ) : null}
      <h3 className="text-base font-bold text-slate-900">Cancelar suscripción</h3>
      <p className="mt-1 text-sm text-slate-500 max-w-xl">
        Conservarás tu plan y tus créditos hasta el final del periodo que ya has pagado. No se
        cobra nada más y no se borra nada de lo que has creado.
      </p>

      {!abierto ? (
        <button data-testid="baja-abrir" onClick={() => setAbierto(true)}
          className="mt-3 rounded-lg border-2 border-slate-300 px-4 py-2 text-sm font-bold text-slate-700 hover:bg-slate-50">
          Cancelar suscripción
        </button>
      ) : (
        <div className="mt-4 border-t border-slate-200 pt-4">
          {/* Una pregunta, no un cuestionario. Cuantos más pasos, menos gente responde de verdad
              y más se limita a pulsar lo primero para salir. */}
          <p className="text-sm font-bold text-slate-900">¿Por qué te vas?</p>
          <p className="mt-0.5 text-sm text-slate-500">
            Nos ayuda a arreglar lo que falla. Tardas menos de un minuto.
          </p>

          <div className="mt-3 grid gap-1.5">
            {motivos.length === 0 && <Loader2 className="animate-spin text-slate-400" size={18} />}
            {motivos.map((m) => (
              <label key={m.id}
                className={`flex items-center gap-2.5 rounded-lg border-2 px-3 py-2.5 cursor-pointer transition-colors ${
                  motivo === m.id ? "border-blue-500 bg-blue-50" : "border-slate-200 hover:bg-slate-50"}`}>
                <input type="radio" name="motivo-baja" value={m.id} checked={motivo === m.id}
                  onChange={() => setMotivo(m.id)} className="accent-blue-600 w-4 h-4 shrink-0" />
                <span className={`text-sm ${motivo === m.id ? "font-bold text-blue-900" : "text-slate-700"}`}>{m.texto}</span>
              </label>
            ))}
          </div>

          <label className="mt-3 block">
            <span className="text-sm font-semibold text-slate-700">¿Algo más que quieras contarnos?</span>
            <textarea value={comentario} onChange={(e) => setComentario(e.target.value)} rows={3}
              maxLength={1000} data-testid="baja-comentario"
              placeholder="Opcional, pero es lo que más nos sirve."
              className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm resize-y" />
          </label>

          <div className="mt-4 flex flex-wrap gap-2">
            <button data-testid="baja-confirmar" onClick={enviar} disabled={!motivo || enviando}
              className="inline-flex items-center gap-2 rounded-lg bg-rose-600 px-4 py-2.5 text-sm font-bold text-white hover:bg-rose-700 disabled:opacity-40 disabled:cursor-not-allowed">
              {enviando ? <Loader2 size={16} className="animate-spin" /> : null}
              Confirmar cancelación
            </button>
            <button onClick={() => { setAbierto(false); setMotivo(""); }}
              className="rounded-lg border-2 border-slate-300 px-4 py-2.5 text-sm font-bold text-slate-700 hover:bg-slate-50">
              Mejor me quedo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
